<div class="about-us">
  <!-- Hero -->
  <section class="about-us__section about-us__section--hero" aria-labelledby="about-us-hero-heading">
    <div
      class="about-us__hero-card flex flex-col gap-8 w-full min-w-0 lg:flex-row lg:items-center lg:gap-12"
    >
      <div class="flex flex-1 flex-col gap-6 min-w-0">
        <div class="about-us__badge about-us__badge--left inline-flex items-center gap-2 self-start">
          <img [src]="heroBadgeIconSrc" alt="" class="about-us__badge-icon" width="18" height="18" />
          <span class="about-us__badge-text">About IUDX Healthcare</span>
        </div>
        <h1 id="about-us-hero-heading" class="about-us__hero-title m-0">
          A Professional Healthcare Data Exchange built for Secure Collaboration and Meaningful Outcomes
        </h1>
        <p class="about-us__hero-lead m-0">
          IUDX Healthcare connects providers, researchers, public health teams and innovators through a trusted
          platform for datasets, models, analytics sandboxes, challenge programs and healthcare-first digital services.
        </p>
        <div class="flex flex-wrap gap-4 items-center">
          <a routerLink="/datasets" class="about-us__btn about-us__btn--primary">Explore datasets</a>
          <a href="#about-us-key-features" class="about-us__btn about-us__btn--secondary">View platform features</a>
        </div>
      </div>
      <div class="flex flex-1 min-w-0 w-full items-center justify-center lg:justify-end">
        <img
          src="/images/about-us-1.png"
          alt="Healthcare professionals reviewing clinical data on a large display"
          class="about-us__hero-image"
          width="596"
          height="535"
          loading="eager"
        />
      </div>
    </div>
  </section>

  <!-- Why this platform matters -->
  <section class="about-us__section" aria-labelledby="about-us-why-heading">
    <header class="about-us__section-header flex flex-col gap-4 items-center text-center max-w-3xl mx-auto mb-10">
      <div class="about-us__badge inline-flex items-center gap-2">
        <img [src]="whyBadgeIconSrc" alt="" class="about-us__badge-icon" width="18" height="18" />
        <span class="about-us__badge-text">Why this platform matters</span>
      </div>
      <h2 id="about-us-why-heading" class="about-us__section-title m-0">
        From healthcare data challenges to practical, scalable solutions
      </h2>
      <p class="about-us__section-sub m-0">
        The platform is designed to reduce fragmentation, improve secure access and create a stronger foundation for
        research, policy and care innovation.
      </p>
    </header>

    <div class="flex flex-col gap-8 lg:flex-row lg:items-stretch lg:gap-10 w-full min-w-0">
      <div class="about-us__why-visual flex flex-1 min-w-0 w-full min-h-0">
        <img
          src="/images/about-us-2.png"
          alt="Clinician using a tablet with advanced medical imaging visualizations"
          class="about-us__why-image"
          width="600"
          height="600"
          loading="lazy"
        />
      </div>

      <div class="about-us__why-panel flex flex-1 flex-col gap-6 min-w-0 w-full p-6 lg:p-8">
        <div class="flex flex-col gap-6">
          @for (row of problemSolutionRows; track row.problemTitle) {
            <div
              class="about-us__ps-row flex flex-col gap-4 sm:flex-row sm:items-stretch sm:gap-3 lg:gap-4"
            >
              <article class="about-us__ps-card about-us__ps-card--problem flex-1 min-w-0">
                <h3 class="about-us__ps-card-title m-0">{{ row.problemTitle }}</h3>
                <p class="about-us__ps-card-body m-0">{{ row.problemBody }}</p>
              </article>
              <div class="about-us__ps-arrow flex items-center justify-center shrink-0 py-2 sm:py-0 sm:px-1">
                <img [src]="arrowIconSrc" alt="" width="20" height="20" class="about-us__arrow-img rotate-90 sm:rotate-0" />
              </div>
              <article class="about-us__ps-card about-us__ps-card--solution flex-1 min-w-0">
                <h3 class="about-us__ps-card-title m-0">{{ row.solutionTitle }}</h3>
                <p class="about-us__ps-card-body m-0">{{ row.solutionBody }}</p>
              </article>
            </div>
          }
        </div>

        <div class="grid grid-cols-1 gap-3 sm:grid-cols-3 sm:gap-4 pt-2">
          @for (pill of techPills; track pill.label) {
            <div class="about-us__tech-pill flex flex-col gap-1 min-w-0 p-4">
              <span class="about-us__tech-pill-label">{{ pill.label }}</span>
              <span class="about-us__tech-pill-desc">{{ pill.description }}</span>
            </div>
          }
        </div>
      </div>
    </div>
  </section>

  <!-- Core capabilities -->
  <section class="about-us__section about-us__section--muted" aria-labelledby="about-us-core-heading">
    <header class="about-us__section-header flex flex-col gap-4 items-center text-center max-w-3xl mx-auto mb-10">
      <div class="about-us__badge inline-flex items-center gap-2">
        <img [src]="coreBadgeIconSrc" alt="" class="about-us__badge-icon" width="16" height="16" />
        <span class="about-us__badge-text">Core capabilities</span>
      </div>
      <h2 id="about-us-core-heading" class="about-us__section-title m-0">Core capabilities of IUDX Healthcare</h2>
      <p class="about-us__section-sub m-0">
        A more professional section structure for the same platform story — using healthcare-relevant icons, stronger
        hierarchy and better scanability.
      </p>
    </header>

    <div class="grid grid-cols-1 gap-4 md:gap-6 lg:grid-cols-2 w-full min-w-0">
      @for (cap of coreCapabilities; track cap.title) {
        <article class="about-us__cap-card flex gap-4 min-w-0 p-5 md:p-6">
          <div class="about-us__icon-sq shrink-0 flex items-center justify-center">
            <img [src]="cap.iconSrc" alt="" width="24" height="24" class="about-us__icon-img" />
          </div>
          <div class="flex flex-col gap-2 min-w-0">
            <h3 class="about-us__cap-title m-0">{{ cap.title }}</h3>
            <p class="about-us__cap-body m-0">{{ cap.body }}</p>
          </div>
        </article>
      }
    </div>
  </section>

  <!-- Key platform features -->
  <section
    id="about-us-key-features"
    class="about-us__section"
    aria-labelledby="about-us-features-heading"
    tabindex="-1"
  >
    <header class="about-us__section-header flex flex-col gap-4 items-center text-center max-w-3xl mx-auto mb-10">
      <div class="about-us__badge inline-flex items-center gap-2">
        <img [src]="featuresBadgeIconSrc" alt="" class="about-us__badge-icon" width="20" height="20" />
        <span class="about-us__badge-text">Key platform features</span>
      </div>
      <h2 id="about-us-features-heading" class="about-us__section-title m-0">Key platform features</h2>
      <p class="about-us__section-sub m-0">
        Explore the core modules of IUDX Healthcare designed to support trusted data discovery, analytics, collaboration
        and innovation across the healthcare ecosystem.
      </p>
    </header>

    <div class="about-us__features-shell p-4 md:p-6 lg:p-8 w-full min-w-0">
      <div class="grid grid-cols-1 gap-4 lg:grid-cols-2 w-full min-w-0">
        @for (feat of keyFeatures; track feat.title) {
          <article
            class="about-us__feature-card flex gap-4 min-w-0 p-5"
            [class.about-us__feature-card--wide]="feat.fullWidth"
          >
            <div class="about-us__icon-sq shrink-0 flex items-center justify-center">
              <img [src]="feat.iconSrc" alt="" width="24" height="24" class="about-us__icon-img" />
            </div>
            <div class="flex flex-col gap-2 min-w-0">
              <h3 class="about-us__feature-title m-0">{{ feat.title }}</h3>
              <p class="about-us__feature-body m-0">{{ feat.body }}</p>
            </div>
          </article>
        }
      </div>
    </div>
  </section>

  <!-- Who benefits -->
  <section
    id="about-us-benefits"
    class="about-us__section about-us__section--benefits"
    aria-labelledby="about-us-benefits-heading"
    tabindex="-1"
  >
    <header class="about-us__section-header flex flex-col gap-4 items-center text-center max-w-3xl mx-auto mb-10">
      <div class="about-us__badge inline-flex items-center gap-2">
        <img
          [src]="benefitsBadgeIconSrc"
          alt=""
          class="about-us__badge-icon"
          width="20"
          height="20"
        />
        <span class="about-us__badge-text">Who benefits</span>
      </div>
      <h2 id="about-us-benefits-heading" class="about-us__section-title m-0">
        Who benefits from IUDX Healthcare
      </h2>
      <p class="about-us__section-sub m-0">
        A dedicated section for key audiences makes the platform easier to understand for institutional buyers,
        partners and end users.
      </p>
    </header>

    <div class="about-us__benefits-cards grid grid-cols-1 gap-4 md:gap-6 lg:grid-cols-4 w-full min-w-0">
      @for (card of benefitCards; track card.title) {
        <article class="about-us__benefit-card p-6 flex flex-col gap-4 min-w-0">
          <div class="about-us__benefit-icon-wrap flex items-center justify-center">
            <img [src]="card.iconSrc" alt="" width="24" height="24" class="about-us__benefit-icon-img" />
          </div>

          <div class="flex flex-col gap-2 min-w-0">
            <h3 class="about-us__benefit-title m-0">{{ card.title }}</h3>
            <p class="about-us__benefit-body m-0">{{ card.body }}</p>
          </div>

          <div class="about-us__benefit-tags flex gap-2 flex-wrap mt-auto">
            @for (tag of card.tags; track tag) {
              <span class="about-us__benefit-tag">{{ tag }}</span>
            }
          </div>
        </article>
      }
    </div>
  </section>

</div>
<app-footer></app-footer>